import { Inject, Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  UrlTree,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthGuardHelper, AuthService } from '@viso/core/data-access';
import { AuthGuardRoutes } from '@viso/core/models';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private auth: AuthService,
    private router: Router,
    @Inject(AuthGuardHelper) private authGuardHelper: AuthGuardRoutes
  ) {}

  private normalizeRoute(route: string): string {
    return route.replace(/^\/|\/$/g, '').split('?')[0];
  }

  private isRouteMatch(route: string, permittedRoutes: string[]): boolean {
    const normalizedRoute = this.normalizeRoute(route);

    return permittedRoutes.some((pattern) => {
      if (pattern.includes(':')) {
        const patternSegments = pattern.split('/');
        const routeSegments = normalizedRoute.split('/');

        if (patternSegments.length !== routeSegments.length) return false;

        return patternSegments.every((segment, i) =>
          segment.startsWith(':') || segment === routeSegments[i]
        );
      }
      return pattern === normalizedRoute;
    });
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const {
      anonymousRoutes,
      beforeLoginDefaultRoute,
      afterLoginDefaultRoute,
      beforeAndAfterLoginPermittedRoute,
    } = this.authGuardHelper;

    const url = route.routeConfig?.path || this.normalizeRoute(state.url);
    const isLoggedIn = this.auth.isLoggedIn();

    const isAnonymousPage = this.isRouteMatch(url, anonymousRoutes);
    const isPermittedPage = this.isRouteMatch(url, beforeAndAfterLoginPermittedRoute);

    if (!isLoggedIn && !isAnonymousPage && !isPermittedPage) {
      return this.router.createUrlTree(beforeLoginDefaultRoute);
    }

    if (
      (isLoggedIn && !isAnonymousPage) ||
      (!isLoggedIn && isAnonymousPage) ||
      isPermittedPage
    ) {
      return true;
    }

    return this.router.createUrlTree(afterLoginDefaultRoute);
  }
}